import {
  CANONICAL_APPOINTMENT_TYPE_KEYS,
  LEGACY_APPOINTMENT_TYPE_KEYS,
  ROOFING_BOT_SETTINGS_DEFAULTS,
  ROOFING_CALL_APPOINTMENT_TYPE,
  ROOFING_VISIT_APPOINTMENT_TYPE,
  ROOFING_DEFAULT_CHANNEL_SCHEDULES,
  cloneRoofingDefaults,
} from "./roofingSettingsDefaults";

const ROOFING_LEGACY_SERVICE_KEYS = new Set([
  "full_roof_replacement",
  "tile_roof_repair",
  "leak_detection_waterproofing",
  "free_roof_inspection_and_quote",
  "emergency_leak_callout",
  "gutter_replacement",
  "solar_install",
  "large_commercial_flat_roofs_over_2000sqm",
  "asbestos_removal",
]);

const ROOFING_LEGACY_SCHEDULING_KEYS = [
  "inspection_hours",
  "meeting_type",
  "default_duration_minutes",
  "buffer_between_meetings",
];

const ROOFING_LEGACY_HANDOFF_KEYS = [
  "lead_agent_name",
  "lead_agent_role",
  "lead_agent_phone",
  "lead_agent_email",
  "allowed_channels",
  "tech_product",
  "sales_partnerships",
];

const APPOINTMENT_MERGE_FIELDS = [
  "duration_minutes",
  "attendee",
  "label",
  "is_free",
  "price",
  "prerequisites",
];

/** Remove knowledge_base.service_area.base_postcode (idempotent). */
export function stripRoofingBasePostcode(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const sa = kbOut.service_area;
    if (sa && typeof sa === "object") {
      const saOut = { ...sa };
      delete saOut.base_postcode;
      kbOut.service_area = saOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove scheduling.operating_hours.public_holidays and legacy flat paths (idempotent). */
export function normalizeRoofingAppointmentTypes(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (!sched || typeof sched !== "object") return out;
  const schedOut = { ...sched };

  const defaults = cloneRoofingDefaults().scheduling?.appointment_types || {};
  const apptRaw = schedOut.appointment_types;
  const apptIn = apptRaw && typeof apptRaw === "object" ? apptRaw : {};

  const visit = { ...(apptIn[ROOFING_VISIT_APPOINTMENT_TYPE] || {}) };
  if (apptIn.on_site_inspection && typeof apptIn.on_site_inspection === "object") {
    for (const field of APPOINTMENT_MERGE_FIELDS) {
      const current = visit[field];
      const empty =
        current === undefined ||
        current === null ||
        current === "" ||
        (Array.isArray(current) && !current.length);
      const val = apptIn.on_site_inspection[field];
      if (empty && val != null && val !== "") {
        visit[field] = val;
      }
    }
  }
  for (const [key, entry] of Object.entries(apptIn)) {
    if (
      CANONICAL_APPOINTMENT_TYPE_KEYS.includes(key) ||
      key === "on_site_inspection" ||
      !entry ||
      typeof entry !== "object"
    ) {
      continue;
    }
    for (const field of APPOINTMENT_MERGE_FIELDS) {
      const current = visit[field];
      const empty =
        current === undefined ||
        current === null ||
        current === "" ||
        (Array.isArray(current) && !current.length);
      if (empty && entry[field] != null && entry[field] !== "") {
        visit[field] = entry[field];
      }
    }
  }
  for (const field of APPOINTMENT_MERGE_FIELDS) {
    const current = visit[field];
    const empty =
      current === undefined ||
      current === null ||
      current === "" ||
      (Array.isArray(current) && !current.length);
    const val = defaults[ROOFING_VISIT_APPOINTMENT_TYPE]?.[field];
    if (empty && val != null && val !== "") {
      visit[field] = val;
    }
  }
  if (visit.is_offered === undefined) visit.is_offered = true;

  const call = { ...(apptIn[ROOFING_CALL_APPOINTMENT_TYPE] || {}) };
  for (const field of APPOINTMENT_MERGE_FIELDS) {
    const current = call[field];
    const empty =
      current === undefined ||
      current === null ||
      current === "" ||
      (Array.isArray(current) && !current.length);
    const val = defaults[ROOFING_CALL_APPOINTMENT_TYPE]?.[field];
    if (empty && val != null && val !== "") {
      call[field] = val;
    }
  }
  call.is_offered = true;
  if (!call.attendee || !String(call.attendee).trim()) {
    call.attendee = "office_staff";
  } else {
    const att = String(call.attendee).trim().toLowerCase();
    if (att !== "office_staff" && att !== "office") {
      call.attendee = "office_staff";
    }
  }

  schedOut.appointment_types = {
    [ROOFING_VISIT_APPOINTMENT_TYPE]: visit,
    [ROOFING_CALL_APPOINTMENT_TYPE]: call,
  };

  const bc =
    schedOut.booking_channel && typeof schedOut.booking_channel === "object"
      ? { ...schedOut.booking_channel }
      : {};
  let visitType = bc.visit_type || ROOFING_VISIT_APPOINTMENT_TYPE;
  if (
    !CANONICAL_APPOINTMENT_TYPE_KEYS.includes(visitType) ||
    visitType === ROOFING_CALL_APPOINTMENT_TYPE ||
    LEGACY_APPOINTMENT_TYPE_KEYS.includes(visitType)
  ) {
    visitType = ROOFING_VISIT_APPOINTMENT_TYPE;
  }
  schedOut.booking_channel = {
    visit_type: visitType,
    call_type: ROOFING_CALL_APPOINTMENT_TYPE,
  };

  out.scheduling = schedOut;
  return out;
}

function pickBestRoofingCalendarId(team) {
  for (const entry of team) {
    if (!entry || typeof entry !== "object") continue;
    if (String(entry.platform || "").trim().toLowerCase() !== "ghl") continue;
    const cid = String(entry.calendar_id || "").trim();
    if (cid) return cid;
  }
  for (const entry of team) {
    if (!entry || typeof entry !== "object") continue;
    const cid = String(entry.calendar_id || "").trim();
    if (cid) return cid;
  }
  return "";
}

function pickRoofingStaffName(team, calendarId) {
  if (calendarId) {
    for (const entry of team) {
      if (!entry || typeof entry !== "object") continue;
      if (String(entry.calendar_id || "").trim() === calendarId) {
        const name = String(entry.staff_name || entry.label || "").trim();
        if (name) return name;
      }
    }
  }
  for (const entry of team) {
    if (!entry || typeof entry !== "object") continue;
    const name = String(entry.staff_name || entry.label || "").trim();
    if (name) return name;
  }
  return "Main GHL Calendar";
}

function buildSingleRoofingTeamCalendarRow(team) {
  const calendarId = pickBestRoofingCalendarId(team);
  const row = {
    staff_name: pickRoofingStaffName(team, calendarId),
    calendar_id: calendarId,
    handles_appointment_types: [...CANONICAL_APPOINTMENT_TYPE_KEYS],
  };
  if (calendarId) {
    row.platform = "ghl";
  }
  return row;
}

/** Collapse scheduling.team_calendars to one row (visit + call on same GHL calendar). */
export function collapseRoofingTeamCalendarsToSingle(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (!sched || typeof sched !== "object") return out;
  const schedOut = { ...sched };
  const teamRaw = schedOut.team_calendars;
  const team = Array.isArray(teamRaw)
    ? teamRaw.filter((e) => e && typeof e === "object").map((e) => ({ ...e }))
    : [];
  schedOut.team_calendars = [buildSingleRoofingTeamCalendarRow(team)];
  out.scheduling = schedOut;
  return out;
}

/** Remove scheduling.operating_hours.public_holidays and legacy flat paths (idempotent). */
export function stripRoofingPublicHolidays(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };

  const stripFromScheduling = (sched) => {
    if (!sched || typeof sched !== "object") return sched;
    const schedOut = { ...sched };
    if (schedOut.public_holidays !== undefined) {
      delete schedOut.public_holidays;
    }
    const oh = schedOut.operating_hours;
    if (oh && typeof oh === "object") {
      const ohOut = { ...oh };
      delete ohOut.public_holidays;
      schedOut.operating_hours = ohOut;
    }
    return schedOut;
  };

  if (out.scheduling) {
    out.scheduling = stripFromScheduling(out.scheduling);
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    if (kbOut.scheduling) {
      kbOut.scheduling = stripFromScheduling(kbOut.scheduling);
    }
    if (kbOut.public_holidays !== undefined) {
      delete kbOut.public_holidays;
    }
    out.knowledge_base = kbOut;
  }
  if (out.public_holidays !== undefined) {
    delete out.public_holidays;
  }
  return out;
}

/** Remove scheduling.operating_hours lunch keys (idempotent). */
export function stripRoofingLunchBreak(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };

  const stripFromScheduling = (sched) => {
    if (!sched || typeof sched !== "object") return sched;
    const schedOut = { ...sched };
    const oh = schedOut.operating_hours;
    if (oh && typeof oh === "object") {
      const ohOut = { ...oh };
      delete ohOut.lunch_break;
      delete ohOut.lunch_breaks;
      schedOut.operating_hours = ohOut;
    }
    return schedOut;
  };

  if (out.scheduling) {
    out.scheduling = stripFromScheduling(out.scheduling);
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    if (kbOut.scheduling) {
      kbOut.scheduling = stripFromScheduling(kbOut.scheduling);
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove scheduling.daylight_rule_enforced (idempotent). */
export function stripRoofingDaylightRule(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };

  const stripFromScheduling = (sched) => {
    if (!sched || typeof sched !== "object") return sched;
    const schedOut = { ...sched };
    delete schedOut.daylight_rule_enforced;
    return schedOut;
  };

  if (out.scheduling) {
    out.scheduling = stripFromScheduling(out.scheduling);
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    if (kbOut.scheduling) {
      kbOut.scheduling = stripFromScheduling(kbOut.scheduling);
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove knowledge_base.operations.office_hours (idempotent). */
export function stripRoofingOfficeHours(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const ops = kbOut.operations;
    if (ops && typeof ops === "object") {
      const opsOut = { ...ops };
      delete opsOut.office_hours;
      kbOut.operations = opsOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove knowledge_base.operations.crew_capacity (idempotent). */
export function stripRoofingCrewCapacity(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const ops = kbOut.operations;
    if (ops && typeof ops === "object") {
      const opsOut = { ...ops };
      delete opsOut.crew_capacity;
      kbOut.operations = opsOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove knowledge_base.operations.field_crew_hours (idempotent). */
export function stripRoofingFieldCrewHours(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const ops = kbOut.operations;
    if (ops && typeof ops === "object") {
      const opsOut = { ...ops };
      delete opsOut.field_crew_hours;
      kbOut.operations = opsOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

const LEGACY_OH_KEYS = ["monday_to_friday", "saturday", "sunday"];
const WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

function normalizeHoursWindow(raw) {
  if (raw == null || typeof raw !== "object") return null;
  if (raw.is_open === false) return null;
  const start = raw.start?.trim?.() || raw.start || null;
  const end = raw.end?.trim?.() || raw.end || null;
  if (!start && !end) return null;
  return { start, end };
}

function hasWeekdayPerDayKeys(raw) {
  if (!raw || typeof raw !== "object") return false;
  return ["monday", "tuesday", "wednesday", "thursday", "friday"].some((key) =>
    Object.prototype.hasOwnProperty.call(raw, key)
  );
}

function normalizeWeeklyDayForUi(raw) {
  if (raw == null) return { is_open: false, start: null, end: null };
  if (typeof raw !== "object") return { is_open: false, start: null, end: null };
  if (raw.is_open === false) return { is_open: false, start: null, end: null };
  const start = raw.start ?? null;
  const end = raw.end ?? null;
  if (!start && !end) return { is_open: false, start: null, end: null };
  return { is_open: raw.is_open !== false, start, end };
}

function deriveWeeklyFromLegacyOh(oh) {
  if (!oh || typeof oh !== "object") return {};
  const mtf = oh.monday_to_friday;
  const weekly = {};
  for (const day of ["monday", "tuesday", "wednesday", "thursday", "friday"]) {
    if (mtf !== undefined) {
      weekly[day] =
        mtf && typeof mtf === "object" ? { ...mtf } : mtf;
    }
  }
  if (oh.saturday !== undefined) {
    weekly.saturday =
      oh.saturday && typeof oh.saturday === "object"
        ? { ...oh.saturday }
        : oh.saturday;
  }
  if (oh.sunday !== undefined) {
    weekly.sunday =
      oh.sunday && typeof oh.sunday === "object" ? { ...oh.sunday } : oh.sunday;
  }
  return weekly;
}

function expandLegacyWeeklySchedule(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = { ...raw };
  const mtf = normalizeHoursWindow(out.monday_to_friday);
  if (!hasWeekdayPerDayKeys(out) && mtf) {
    for (const day of ["monday", "tuesday", "wednesday", "thursday", "friday"]) {
      if (!(day in out)) out[day] = { ...mtf };
    }
  }
  delete out.monday_to_friday;
  return out;
}

function expandOperatingHoursWeekly(oh) {
  if (!oh || typeof oh !== "object") {
    return { weekly: Object.fromEntries(WEEKDAY_KEYS.map((k) => [k, normalizeWeeklyDayForUi(null)])) };
  }
  const ohOut = { ...oh };
  let weekly = { ...(ohOut.weekly || {}) };
  weekly = expandLegacyWeeklySchedule(weekly);
  if (!hasWeekdayPerDayKeys(weekly)) {
    weekly = { ...deriveWeeklyFromLegacyOh(ohOut), ...weekly };
  }
  const normalizedWeekly = {};
  for (const key of WEEKDAY_KEYS) {
    normalizedWeekly[key] = normalizeWeeklyDayForUi(weekly[key]);
  }
  ohOut.weekly = normalizedWeekly;
  for (const key of LEGACY_OH_KEYS) {
    delete ohOut[key];
  }
  return ohOut;
}

/** Expand legacy monday_to_friday/saturday/sunday into operating_hours.weekly (idempotent). */
export function normalizeRoofingOperatingHoursWeekly(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (!sched || typeof sched !== "object") return out;
  const schedOut = { ...sched };
  if (schedOut.operating_hours && typeof schedOut.operating_hours === "object") {
    schedOut.operating_hours = expandOperatingHoursWeekly(schedOut.operating_hours);
  }
  out.scheduling = schedOut;
  return out;
}

const CHANNEL_SITE_VISIT = "site_visit";
const CHANNEL_PHONE_CONSULT = "phone_consult";

function scheduleHasHours(sched) {
  if (!sched || typeof sched !== "object") return false;
  if (normalizeHoursWindow(sched.monday_to_friday)) return true;
  return WEEKDAY_KEYS.some((k) => normalizeHoursWindow(sched[k]));
}

function weeklyToChannelSchedule(weekly) {
  if (!weekly || typeof weekly !== "object") return null;
  const out = {};
  for (const day of WEEKDAY_KEYS) {
    const window = normalizeHoursWindow(weekly[day]);
    if (window) out[day] = window;
  }
  return Object.keys(out).length ? out : null;
}

function deriveSiteVisitFromOperatingHours(oh) {
  if (!oh || typeof oh !== "object") return null;
  if (oh.monday_to_friday != null || oh.saturday != null || oh.sunday != null) {
    const out = {};
    const mtf = normalizeHoursWindow(oh.monday_to_friday);
    if (mtf) out.monday_to_friday = mtf;
    if (oh.saturday != null) out.saturday = normalizeHoursWindow(oh.saturday);
    if (oh.sunday != null) out.sunday = normalizeHoursWindow(oh.sunday);
    return Object.keys(out).length ? out : null;
  }
  return weeklyToChannelSchedule(oh.weekly);
}

function normalizeChannelScheduleForUi(raw) {
  const expanded = expandLegacyWeeklySchedule(raw && typeof raw === "object" ? raw : {});
  const out = {};
  for (const key of WEEKDAY_KEYS) {
    out[key] = normalizeWeeklyDayForUi(expanded[key]);
  }
  return out;
}

/** Populate scheduling.channel_schedules from legacy paths (idempotent). */
export function backfillRoofingChannelSchedules(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling && typeof out.scheduling === "object" ? { ...out.scheduling } : {};
  const existing = sched.channel_schedules;
  const cs = existing && typeof existing === "object" ? { ...existing } : {};

  if (!scheduleHasHours(cs[CHANNEL_SITE_VISIT])) {
    const derived = deriveSiteVisitFromOperatingHours(sched.operating_hours);
    if (derived) cs[CHANNEL_SITE_VISIT] = derived;
  }

  if (!scheduleHasHours(cs[CHANNEL_PHONE_CONSULT])) {
    cs[CHANNEL_PHONE_CONSULT] = {
      ...ROOFING_DEFAULT_CHANNEL_SCHEDULES.phone_consult,
    };
  }

  if (Object.keys(cs).length) {
    const siteNorm = normalizeChannelScheduleForUi(cs[CHANNEL_SITE_VISIT]);
    delete siteNorm.out_of_hours_message;
    const phoneNorm = normalizeChannelScheduleForUi(cs[CHANNEL_PHONE_CONSULT]);
    delete phoneNorm.out_of_hours_message;
    sched.channel_schedules = {
      [CHANNEL_SITE_VISIT]: siteNorm,
      [CHANNEL_PHONE_CONSULT]: phoneNorm,
    };
  }
  out.scheduling = sched;
  return out;
}

/** Normalize per-day channel schedules (idempotent). */
export function normalizeRoofingChannelSchedulesPerDay(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling && typeof out.scheduling === "object" ? { ...out.scheduling } : null;
  if (!sched?.channel_schedules) return out;
  const cs = { ...sched.channel_schedules };
  for (const channel of [CHANNEL_SITE_VISIT, CHANNEL_PHONE_CONSULT]) {
    const normalized = normalizeChannelScheduleForUi(cs[channel]);
    delete normalized.out_of_hours_message;
    cs[channel] = normalized;
  }
  sched.channel_schedules = cs;
  out.scheduling = sched;
  return out;
}

/** Strip legacy hour keys after channel_schedules backfill (idempotent). */
export function stripRoofingLegacyHours(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const oh = schedOut.operating_hours;
    if (oh && typeof oh === "object") {
      const ohOut = { ...oh };
      delete ohOut.weekly;
      for (const key of LEGACY_OH_KEYS) delete ohOut[key];
      schedOut.operating_hours = ohOut;
    }
    out.scheduling = schedOut;
  }
  return out;
}

/** Remove scheduling.booking_rules.weekend_auto_confirm (idempotent). */
export function stripRoofingWeekendAutoConfirm(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const br = schedOut.booking_rules;
    if (br && typeof br === "object") {
      const brOut = { ...br };
      delete brOut.weekend_auto_confirm;
      schedOut.booking_rules = brOut;
    }
    out.scheduling = schedOut;
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const kbSched = kbOut.scheduling;
    if (kbSched && typeof kbSched === "object") {
      const kbSchedOut = { ...kbSched };
      const br = kbSchedOut.booking_rules;
      if (br && typeof br === "object") {
        const brOut = { ...br };
        delete brOut.weekend_auto_confirm;
        kbSchedOut.booking_rules = brOut;
      }
      kbOut.scheduling = kbSchedOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

const HANDOFF_ROLE_CONTACT_KEYS = ["role", "name", "phone", "email"];
const HANDOFF_COLLAPSE_TYPE_ORDER = ["general", "emergency", "warranty", "commercial"];
const HANDOFF_ROLE_STRIP_KEYS = new Set([
  "priority",
  "allowed_channels",
  "availability_hours",
  "after_hours_action",
  "min_project_size",
]);

function handoffRoleHasContact(role) {
  if (!role || typeof role !== "object") return false;
  return HANDOFF_ROLE_CONTACT_KEYS.some((key) => String(role[key] || "").trim());
}

function mergeHandoffRoleFields(...roles) {
  const merged = { type: "general" };
  for (const key of HANDOFF_ROLE_CONTACT_KEYS) {
    for (const role of roles) {
      if (!role || typeof role !== "object") continue;
      const val = String(role[key] || "").trim();
      if (val) {
        merged[key] = val;
        break;
      }
    }
  }
  return merged;
}

function resolvePrimaryHandoffRoleFromSettings(handoffSettings) {
  const hs = handoffSettings && typeof handoffSettings === "object" ? handoffSettings : {};
  const rawRoles = hs.roles;
  const rolesList = Array.isArray(rawRoles)
    ? rawRoles.filter((r) => r && typeof r === "object")
    : [];

  let general = null;
  const byType = new Map();
  rolesList.forEach((role) => {
    const roleType = String(role.type || "").toLowerCase();
    if (roleType) byType.set(roleType, role);
    if (roleType === "general") general = role;
  });

  const ordered = [];
  HANDOFF_COLLAPSE_TYPE_ORDER.forEach((roleType) => {
    if (byType.has(roleType)) ordered.push(byType.get(roleType));
  });
  rolesList.forEach((role) => {
    const roleType = String(role.type || "").toLowerCase();
    if (roleType && !HANDOFF_COLLAPSE_TYPE_ORDER.includes(roleType)) {
      ordered.push(role);
    }
  });

  if (ordered.length) {
    const merged = mergeHandoffRoleFields(...ordered);
    if (handoffRoleHasContact(merged)) return merged;
  }

  if (general) return { ...general };

  for (const role of rolesList) {
    if (handoffRoleHasContact(role)) return { ...role };
  }

  const lead = {};
  const legacyMap = [
    ["lead_agent_name", "name"],
    ["lead_agent_role", "role"],
    ["lead_agent_phone", "phone"],
    ["lead_agent_email", "email"],
  ];
  legacyMap.forEach(([src, dst]) => {
    const val = String(hs[src] || "").trim();
    if (val) lead[dst] = val;
  });
  if (Object.keys(lead).length) {
    return { type: "general", ...lead };
  }

  return { type: "general" };
}

function normalizeHandoffRoles(rawRoles, defaultRoles) {
  const defaults = defaultRoles || [];
  const defaultGeneral =
    defaults.find((r) => String(r?.type).toLowerCase() === "general") || defaults[0] || {
      type: "general",
    };
  const incoming = Array.isArray(rawRoles) ? rawRoles : [];
  const byType = new Map();
  incoming.forEach((r) => {
    if (r?.type) {
      byType.set(String(r.type).toLowerCase(), { ...r, type: String(r.type).toLowerCase() });
    }
  });

  const mergedIncoming = HANDOFF_COLLAPSE_TYPE_ORDER.map((key) => byType.get(key)).filter(Boolean);
  byType.forEach((role, key) => {
    if (!HANDOFF_COLLAPSE_TYPE_ORDER.includes(key)) mergedIncoming.push(role);
  });

  const primary = mergeHandoffRoleFields(
    resolvePrimaryHandoffRoleFromSettings({ roles: mergedIncoming }),
    defaultGeneral,
    ...mergedIncoming
  );
  const collapsed = { ...defaultGeneral, ...primary, type: "general" };
  HANDOFF_ROLE_STRIP_KEYS.forEach((key) => {
    delete collapsed[key];
  });
  return [collapsed];
}

/** Reduce handoff_settings.roles[] to single primary role (idempotent). */
export function collapseRoofingHandoffRoles(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (!kb || typeof kb !== "object") return out;
  const kbOut = { ...kb };
  const hs = kbOut.handoff_settings;
  if (!hs || typeof hs !== "object") return out;
  const hsOut = { ...hs };
  const primary = resolvePrimaryHandoffRoleFromSettings(hsOut);
  const collapsed = { type: "general" };
  HANDOFF_ROLE_CONTACT_KEYS.forEach((key) => {
    const val = String(primary[key] || "").trim();
    if (val) collapsed[key] = val;
  });
  hsOut.roles = [collapsed];
  kbOut.handoff_settings = hsOut;
  out.knowledge_base = kbOut;
  return out;
}

/** Remove payment_and_finance.financing_options (idempotent). */
export function stripRoofingFinancingOptions(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const pp = kbOut.pricing_policy;
    if (pp && typeof pp === "object") {
      const ppOut = { ...pp };
      const paf = ppOut.payment_and_finance;
      if (paf && typeof paf === "object") {
        const pafOut = { ...paf };
        delete pafOut.financing_options;
        ppOut.payment_and_finance = pafOut;
      }
      kbOut.pricing_policy = ppOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove ballpark_pricing.enforce_no_exact_prices (idempotent). */
export function stripRoofingEnforceNoExactPrices(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const legacyBp = kbOut.baseline_pricing;
    if (legacyBp && typeof legacyBp === "object") {
      const legacyBpOut = { ...legacyBp };
      delete legacyBpOut.enforce_no_exact_prices;
      kbOut.baseline_pricing = legacyBpOut;
    }
    const pp = kbOut.pricing_policy;
    if (pp && typeof pp === "object") {
      const ppOut = { ...pp };
      const bp = ppOut.ballpark_pricing;
      if (bp && typeof bp === "object") {
        const bpOut = { ...bp };
        delete bpOut.enforce_no_exact_prices;
        ppOut.ballpark_pricing = bpOut;
      }
      kbOut.pricing_policy = ppOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove qualification_logic.internal_goal_summary (idempotent). */
export function stripRoofingInternalGoalSummary(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const ql = out.qualification_logic;
  if (ql && typeof ql === "object") {
    const qlOut = { ...ql };
    delete qlOut.internal_goal_summary;
    out.qualification_logic = qlOut;
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const kbQl = kbOut.qualification_logic;
    if (kbQl && typeof kbQl === "object") {
      const kbQlOut = { ...kbQl };
      delete kbQlOut.internal_goal_summary;
      kbOut.qualification_logic = kbQlOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove qualification_logic.skip_qualification_if_known_user (idempotent). */
export function stripRoofingSkipQualificationIfKnownUser(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const ql = out.qualification_logic;
  if (ql && typeof ql === "object") {
    const qlOut = { ...ql };
    delete qlOut.skip_qualification_if_known_user;
    out.qualification_logic = qlOut;
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const kbQl = kbOut.qualification_logic;
    if (kbQl && typeof kbQl === "object") {
      const kbQlOut = { ...kbQl };
      delete kbQlOut.skip_qualification_if_known_user;
      kbOut.qualification_logic = kbQlOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove tagline from all known roofing paths (idempotent). */
export function stripRoofingTagline(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  delete out.tagline;

  const persona = out.persona;
  if (persona && typeof persona === "object") {
    const personaOut = { ...persona };
    delete personaOut.tagline;
    out.persona = personaOut;
  }

  const tov = out.tone_of_voice;
  if (tov && typeof tov === "object") {
    const tovOut = { ...tov };
    delete tovOut.tagline;
    out.tone_of_voice = tovOut;
  }

  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    delete kbOut.tagline;
    const personaKb = kbOut.persona;
    if (personaKb && typeof personaKb === "object") {
      const personaKbOut = { ...personaKb };
      delete personaKbOut.tagline;
      kbOut.persona = personaKbOut;
    }
    const brandSettings = kbOut.brand_settings;
    if (brandSettings && typeof brandSettings === "object") {
      const brandOut = { ...brandSettings };
      delete brandOut.tagline;
      kbOut.brand_settings = brandOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove scheduling.logistics.max_appointments_per_day (idempotent). */
export function stripRoofingMaxAppointmentsPerDay(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const logistics = schedOut.logistics;
    if (logistics && typeof logistics === "object") {
      const logisticsOut = { ...logistics };
      delete logisticsOut.max_appointments_per_day;
      schedOut.logistics = logisticsOut;
    }
    out.scheduling = schedOut;
  }
  return out;
}

/** Remove scheduling.booking_rules.allow_double_booking (idempotent). */
export function stripRoofingAllowDoubleBooking(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const br = schedOut.booking_rules;
    if (br && typeof br === "object") {
      const brOut = { ...br };
      delete brOut.allow_double_booking;
      schedOut.booking_rules = brOut;
    }
    out.scheduling = schedOut;
  }
  return out;
}

function popBookingRuleKey(out, key) {
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const br = schedOut.booking_rules;
    if (br && typeof br === "object") {
      const brOut = { ...br };
      delete brOut[key];
      schedOut.booking_rules = brOut;
    }
    out.scheduling = schedOut;
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const kbSched = kbOut.scheduling;
    if (kbSched && typeof kbSched === "object") {
      const kbSchedOut = { ...kbSched };
      const br = kbSchedOut.booking_rules;
      if (br && typeof br === "object") {
        const brOut = { ...br };
        delete brOut[key];
        kbSchedOut.booking_rules = brOut;
      }
      kbOut.scheduling = kbSchedOut;
    }
    out.knowledge_base = kbOut;
  }
}

function backfillBookingRulesMinLeadByChannel(br) {
  if (!br || typeof br !== "object") return br;
  const out = { ...br };
  const legacy = out.min_lead_time_hours;
  if (legacy != null && legacy !== "") {
    out.visit_min_lead_time_hours = parseInt(legacy, 10) || 24;
  } else if (out.visit_min_lead_time_hours == null) {
    out.visit_min_lead_time_hours = 24;
  }
  if (out.call_min_lead_time_hours == null) {
    out.call_min_lead_time_hours = 4;
  }
  return out;
}

function backfillBookingRulesMinLeadInDoc(doc) {
  if (!doc || typeof doc !== "object") return doc;
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const br = schedOut.booking_rules;
    if (br && typeof br === "object") {
      schedOut.booking_rules = backfillBookingRulesMinLeadByChannel(br);
    }
    out.scheduling = schedOut;
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const kbSched = kbOut.scheduling;
    if (kbSched && typeof kbSched === "object") {
      const kbSchedOut = { ...kbSched };
      const br = kbSchedOut.booking_rules;
      if (br && typeof br === "object") {
        kbSchedOut.booking_rules = backfillBookingRulesMinLeadByChannel(br);
      }
      kbOut.scheduling = kbSchedOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Backfill visit/call min lead time from legacy min_lead_time_hours. */
export function backfillRoofingMinLeadTimeByChannel(doc) {
  return backfillBookingRulesMinLeadInDoc(doc);
}

/** Remove scheduling.booking_rules.min_lead_time_hours (idempotent). */
export function stripRoofingLegacyMinLeadTimeHours(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  popBookingRuleKey(out, "min_lead_time_hours");
  return out;
}

/** Remove knowledge_base.operations.quoting_workflow.description (idempotent). */
export function stripRoofingQuotingWorkflowDescription(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const ops = kbOut.operations;
    if (ops && typeof ops === "object") {
      const opsOut = { ...ops };
      const qw = opsOut.quoting_workflow;
      if (qw && typeof qw === "object") {
        const qwOut = { ...qw };
        delete qwOut.description;
        opsOut.quoting_workflow = qwOut;
      }
      kbOut.operations = opsOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Remove channel_schedules.*.out_of_hours_message (idempotent). */
export function stripRoofingOutOfHoursMessage(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    const cs = schedOut.channel_schedules;
    if (cs && typeof cs === "object") {
      const csOut = { ...cs };
      for (const channel of [CHANNEL_SITE_VISIT, CHANNEL_PHONE_CONSULT]) {
        const ch = csOut[channel];
        if (ch && typeof ch === "object") {
          const chOut = { ...ch };
          delete chOut.out_of_hours_message;
          csOut[channel] = chOut;
        }
      }
      schedOut.channel_schedules = csOut;
    }
    out.scheduling = schedOut;
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const ops = kbOut.operations;
    if (ops && typeof ops === "object") {
      const opsOut = { ...ops };
      const oh = opsOut.office_hours;
      if (oh && typeof oh === "object") {
        const ohOut = { ...oh };
        delete ohOut.out_of_hours_message;
        opsOut.office_hours = ohOut;
      }
      kbOut.operations = opsOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

/** Move baseline_pricing.products into pricing_policy.ballpark_pricing when empty. */
export function backfillRoofingBaselinePricingToBallpark(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (!kb || typeof kb !== "object") return out;
  const kbOut = { ...kb };
  const baseline = kbOut.baseline_pricing;
  if (!baseline || typeof baseline !== "object") return out;
  const products = baseline.products;
  if (!Array.isArray(products) || !products.length) return out;
  const pp = { ...(kbOut.pricing_policy || {}) };
  const bp = { ...(pp.ballpark_pricing || {}) };
  const existing = bp.products;
  if (!Array.isArray(existing) || !existing.length) {
    bp.products = [...products];
    pp.ballpark_pricing = bp;
    kbOut.pricing_policy = pp;
    out.knowledge_base = kbOut;
  }
  return out;
}

export function stripRoofingBaselinePricing(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    delete kbOut.baseline_pricing;
    out.knowledge_base = kbOut;
  }
  return out;
}

export function stripRoofingAgencyName(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    delete kbOut.agency_name;
    out.knowledge_base = kbOut;
  }
  return out;
}

export function hoistRoofingOutOfScopeServices(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (!kb || typeof kb !== "object") return out;
  const kbOut = { ...kb };
  const rootOos = kbOut.out_of_scope_services;
  delete kbOut.out_of_scope_services;
  out.knowledge_base = kbOut;
  if (!Array.isArray(rootOos) || !rootOos.length) return out;
  const ql = { ...(out.qualification_logic || {}) };
  const dr = { ...(ql.disqualification_rules || {}) };
  const existing = Array.isArray(dr.out_of_scope_services) ? dr.out_of_scope_services : [];
  const seen = new Set(existing.map((s) => String(s).trim()).filter(Boolean));
  const merged = [...existing];
  for (const item of rootOos) {
    const key = String(item).trim();
    if (key && !seen.has(key)) {
      seen.add(key);
      merged.push(key);
    }
  }
  dr.out_of_scope_services = merged;
  ql.disqualification_rules = dr;
  out.qualification_logic = ql;
  return out;
}

export function stripRoofingLegacyServiceKeys(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (!kb || typeof kb !== "object") return out;
  const kbOut = { ...kb };
  const services = kbOut.services_offered;
  if (services && typeof services === "object" && !Array.isArray(services)) {
    const servicesOut = { ...services };
    for (const key of ROOFING_LEGACY_SERVICE_KEYS) {
      delete servicesOut[key];
    }
    kbOut.services_offered = servicesOut;
  }
  out.knowledge_base = kbOut;
  return out;
}

export function stripRoofingLegacySchedulingFields(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const sched = out.scheduling;
  if (sched && typeof sched === "object") {
    const schedOut = { ...sched };
    for (const key of ROOFING_LEGACY_SCHEDULING_KEYS) {
      delete schedOut[key];
    }
    out.scheduling = schedOut;
  }
  const kb = out.knowledge_base;
  if (kb && typeof kb === "object") {
    const kbOut = { ...kb };
    const kbSched = kbOut.scheduling;
    if (kbSched && typeof kbSched === "object") {
      const kbSchedOut = { ...kbSched };
      for (const key of ROOFING_LEGACY_SCHEDULING_KEYS) {
        delete kbSchedOut[key];
      }
      kbOut.scheduling = kbSchedOut;
    }
    out.knowledge_base = kbOut;
  }
  return out;
}

export function stripRoofingLegacyHandoffFields(doc) {
  if (!doc || typeof doc !== "object") return {};
  const out = { ...doc };
  const kb = out.knowledge_base;
  if (!kb || typeof kb !== "object") return out;
  const kbOut = { ...kb };
  const hs = kbOut.handoff_settings;
  if (hs && typeof hs === "object") {
    const hsOut = { ...hs };
    for (const key of ROOFING_LEGACY_HANDOFF_KEYS) {
      delete hsOut[key];
    }
    kbOut.handoff_settings = hsOut;
  }
  out.knowledge_base = kbOut;
  return out;
}

function deepMerge(target, source) {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return target;
  }
  const out = { ...target };
  for (const [key, val] of Object.entries(source)) {
    if (
      val &&
      typeof val === "object" &&
      !Array.isArray(val) &&
      out[key] &&
      typeof out[key] === "object" &&
      !Array.isArray(out[key])
    ) {
      out[key] = deepMerge(out[key], val);
    } else if (val !== undefined) {
      out[key] = val;
    }
  }
  return out;
}

function mergeRoofingWithDefaults(raw, defaults) {
  const base = cloneRoofingDefaults();
  const d = defaults || ROOFING_BOT_SETTINGS_DEFAULTS;
  const merged = {
    bot_type: raw.bot_type || d.bot_type || base.bot_type,
    knowledge_base: deepMerge(
      deepMerge(base.knowledge_base, d.knowledge_base || {}),
      raw.knowledge_base || {}
    ),
    qualification_logic: deepMerge(
      deepMerge(base.qualification_logic, d.qualification_logic || {}),
      raw.qualification_logic || {}
    ),
    scheduling: deepMerge(
      deepMerge(base.scheduling, d.scheduling || {}),
      raw.scheduling || {}
    ),
    tone_of_voice: deepMerge(
      deepMerge(base.tone_of_voice, d.tone_of_voice || {}),
      raw.tone_of_voice || {}
    ),
  };
  const kb = merged.knowledge_base || {};
  const rawProducts = kb.baseline_pricing?.products;
  const ballpark = kb.pricing_policy?.ballpark_pricing?.products;
  if (Array.isArray(rawProducts) && rawProducts.length && (!Array.isArray(ballpark) || !ballpark.length)) {
    merged.knowledge_base = {
      ...kb,
      pricing_policy: {
        ...(kb.pricing_policy || {}),
        ballpark_pricing: {
          ...(kb.pricing_policy?.ballpark_pricing || {}),
          products: rawProducts,
        },
      },
    };
  }
  const rootOos = merged.knowledge_base?.out_of_scope_services;
  const nestedOos = merged.qualification_logic?.disqualification_rules?.out_of_scope_services;
  if (
    Array.isArray(rootOos) &&
    rootOos.length &&
    (!Array.isArray(nestedOos) || !nestedOos.length)
  ) {
    merged.qualification_logic = {
      ...(merged.qualification_logic || {}),
      disqualification_rules: {
        ...(merged.qualification_logic?.disqualification_rules || {}),
        out_of_scope_services: rootOos,
      },
    };
  }
  if (Array.isArray(merged.qualification_logic?.questions) && merged.qualification_logic.questions.length) {
    merged.qualification_logic.questions = merged.qualification_logic.questions;
  } else if (Array.isArray(d.qualification_logic?.questions)) {
    merged.qualification_logic.questions = d.qualification_logic.questions;
  }
  return merged;
}

/** Entry point for load/save: strip legacy fields and normalize appointment types. */
export function normalizeRoofingSettings(raw, defaults = null) {
  if (!raw || typeof raw !== "object") return {};
  const doc = defaults ? mergeRoofingWithDefaults(raw, defaults) : raw;
  return stripRoofingOutOfHoursMessage(
    stripRoofingQuotingWorkflowDescription(
    stripRoofingLegacyMinLeadTimeHours(
    backfillRoofingMinLeadTimeByChannel(
      stripRoofingLegacyHandoffFields(
      collapseRoofingHandoffRoles(
    collapseRoofingTeamCalendarsToSingle(
    normalizeRoofingAppointmentTypes(
      stripRoofingFinancingOptions(
        stripRoofingEnforceNoExactPrices(
          stripRoofingInternalGoalSummary(
          stripRoofingSkipQualificationIfKnownUser(
          stripRoofingTagline(
            stripRoofingWeekendAutoConfirm(
            stripRoofingAllowDoubleBooking(
              stripRoofingMaxAppointmentsPerDay(
                stripRoofingLegacyHours(
                  normalizeRoofingChannelSchedulesPerDay(
                    backfillRoofingChannelSchedules(
                      normalizeRoofingOperatingHoursWeekly(
                        stripRoofingFieldCrewHours(
                          stripRoofingCrewCapacity(
                            stripRoofingOfficeHours(
                              stripRoofingDaylightRule(
                                stripRoofingLunchBreak(
                                  stripRoofingPublicHolidays(
                                    stripRoofingLegacySchedulingFields(
                                      stripRoofingLegacyServiceKeys(
                                        stripRoofingAgencyName(
                                          stripRoofingBaselinePricing(
                                            hoistRoofingOutOfScopeServices(
                                              backfillRoofingBaselinePricingToBallpark(
                                                stripRoofingBasePostcode(doc)
                                              )
                                            )
                                          )
                                        )
                                      )
                                    )
                                  )
                                )
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
          )
        )
        )
      )
      )
    )
      )
    )
    )
    )
  )));
}
